import { ChangeEvent, Fragment, ReactNode, SyntheticEvent, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"
import FieldError, { FormErrors, FormErrorsArr } from "../../FieldError"
import { classNames } from "primereact/utils"
import { useOrderTemplateDetail } from "../../../api/queries/order-templates/useOrderTemplateDetail"
import { useOrderTemplateUpdate } from "../../../api/queries/order-templates/useOrderTemplateUpdate"
import { CreateOrderTemplatePayload, OrderCalculationTemplate, OrderGoodsTemplate, OrderLoadingTemplate, OrderTemplate, OrderUnloadingTemplate, UpdateOrderTemplatePayload } from "../../../api/types/order-template"
import { useOrderTemplateCreate } from "../../../api/queries/order-templates/useOrderTemplateCreate"
import { useOrderTemplateDelete } from "../../../api/queries/order-templates/useOrderTemplateDelete"
import { validate } from "./validate"
import { Dropdown, DropdownChangeParams, DropdownProps } from "primereact/dropdown"
import { useConstants } from "../../../api/queries/useConstants"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import { PhoneInput } from "../../common/PhoneInput"
import { InputNumber } from "primereact/inputnumber"
import { GetOrderGoodsPackingsResponseData, GoodsPacking, OrderCalculation, OrderCalculationCod, OrderGoods, OrderLoading, OrderUnloading } from "../../../api/types/order"
import { useGoodsPackings } from "../../../api/queries/useGoodsPackings"
import { InputTextarea } from "primereact/inputtextarea"
import { RadioButton } from "primereact/radiobutton"
import commonStyles from "../../common.module.css"
import { useOrderTemplateCreateOrder } from "../../../api/queries/order-templates/useOrderTemplateCreateOrder"
import { splitNumber } from "../../common/PhoneInput/utils"
import { AddressSimple } from "../../../api/types/address"
import AddressBook from "../../AddressBook"
import { MAX_GOODS_COUNT } from "../../Order/Form/Goods"
import orderFormStyles from "../../Order/Form/styles.module.css"
import { scrollToFirstInvalidElement } from "../../../common/util"
import { TimeRange } from "../../common/TimeRange"
import { OrderTemplateError } from "../../../api/types/base"
import { countryItemTemplate, countryValueTemplate } from "../../common/country"

export type OrderTemplateParams = "uuid"

export default function OrderTemplateForm() {
  const navigate = useNavigate()

  const { uuid } = useParams<OrderTemplateParams>()
  const { data: orderTemplateDetail } = useOrderTemplateDetail(uuid)
  const { data: constants } = useConstants()

  const createOrderTemplate = useOrderTemplateCreate()
  const updateOrderTemplate = useOrderTemplateUpdate(uuid)
  const deleteOrderTemplate = useOrderTemplateDelete(uuid)
  const createOrderTemplateOrder = useOrderTemplateCreateOrder(uuid)

  useEffect(() => {
    if (orderTemplateDetail && constants) {
      let {
        name,
        loading,
        unloading,
        goods,
        calculation
      } = orderTemplateDetail.results.orderTemplateDetail

      if (!calculation.loadingTime) {
        calculation.loadingTime = "08:00-16:00"
      }

      setName(name)
      setLoading(loading)
      setUnloading(unloading)
      setGoods(goods || [])
      setCalculation(calculation)

      if (loading.phone) {
        const { prefix, number } = splitNumber(constants.results.countries, loading.phone)
        setLoadingPhonePrefix(prefix)
        setLoadingPhone(number)
      }

      if (loading.mobile) {
        const { prefix, number } = splitNumber(constants.results.countries, loading.mobile)
        setLoadingMobilePrefix(prefix)
        setLoadingMobile(number)
      }

      if (unloading.phone) {
        const { prefix, number } = splitNumber(constants.results.countries, unloading.phone)
        setUnloadingPhonePrefix(prefix)
        setUnloadingPhone(number)
      }

      if (unloading.mobile) {
        const { prefix, number } = splitNumber(constants.results.countries, unloading.mobile)
        setUnloadingMobilePrefix(prefix)
        setUnloadingMobile(number)
      }

      if (calculation.cod) {
        if (calculation.cod.amount || calculation.cod.bankAccount || calculation.cod.vs) {
          setCod(true)
        }
      }
    }
  }, [orderTemplateDetail, constants])

  const [name, setName] = useState<string>("")
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    resetErrorsFor(["name"])
  }
  const [templateErrors, setTemplateErrors] = useState<FormErrors<OrderTemplate>>()
  const [loadingErrors, setLoadingErrors] = useState<FormErrors<OrderLoading>>()
  const [unloadingErrors, setUnloadingErrors] = useState<FormErrors<OrderUnloading>>()
  const [goodsErrors, setGoodsErrors] = useState<FormErrorsArr<OrderGoods>>([])
  const [calculationErrors, setCalculationErrors] = useState<FormErrors<OrderCalculation>>()
  const [calculationCodErrors, setCalculationCodErrors] = useState<FormErrors<OrderCalculationCod>>()

  const [loading, setLoading] = useState<OrderLoadingTemplate>({
    name: "",
    street: "",
    streetDetails: "",
    zip: "",
    city: "",
    country: "",
    person: "",
    phone: "",
    mobile: "",
    email: "",
    notifyPhone: false,
    reference: "",
    note: "",
    tailgate: false,
  })

  const [loadingPhonePrefix, setLoadingPhonePrefix] = useState<string>("")
  const [loadingPhone, setLoadingPhone] = useState<number | null>(null)
  const handleLoadingPhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setLoadingPhonePrefix(prefix)
    setLoadingPhone(value)
    if (prefix && value) {
      handleLoadingChange("phone", `${prefix}${value}`)
    }
    resetLoadingErrorsFor(["phone", "mobile"])
  }

  const [loadingMobilePrefix, setLoadingMobilePrefix] = useState<string>("")
  const [loadingMobile, setLoadingMobile] = useState<number | null>(null)
  const handleLoadingMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setLoadingMobilePrefix(prefix)
    setLoadingMobile(value)
    if (prefix && value) {
      handleLoadingChange("mobile", `${prefix}${value}`)
    }
    resetLoadingErrorsFor(["phone", "mobile"])
  }

  const handleLoadingCountryChange = (event: DropdownChangeParams) => {
    handleLoadingChange("country", event.value)

    const prefix = constants?.results.countries
      .find((country) => country.value === event.value)?.prefix

    if (prefix) {
      setLoadingPhonePrefix(prefix)
      setLoadingMobilePrefix(prefix)
    }
  }

  const handleLoadingChange = (key: keyof OrderLoadingTemplate, value: any) => {
    setLoading(Object.assign({}, loading, { [key]: value }))
    resetLoadingErrorsFor([key])
  }

  const [unloading, setUnloading] = useState<OrderUnloadingTemplate>({
    name: "",
    street: "",
    streetDetails: "",
    zip: "",
    city: "",
    country: "",
    person: "",
    phone: "",
    mobile: "",
    email: "",
    notifyPhone: false,
    notifySMS: false,
    notifyEmail: false,
    reference: "",
    note: "",
    tailgate: false,
  })

  const [unloadingPhonePrefix, setUnloadingPhonePrefix] = useState<string>("")
  const [unloadingPhone, setUnloadingPhone] = useState<number | null>(null)
  const handleUnloadingPhoneChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setUnloadingPhonePrefix(prefix)
    setUnloadingPhone(value)
    if (prefix && value) {
      handleUnloadingChange("phone", `${prefix}${value}`)
    }
    resetUnloadingErrorsFor(["phone", "mobile"])
  }

  const [unloadingMobilePrefix, setUnloadingMobilePrefix] = useState<string>("")
  const [unloadingMobile, setUnloadingMobile] = useState<number | null>(null)
  const handleUnloadingMobileChange = ({ prefix, value }: { prefix: string, value: number | null }) => {
    setUnloadingMobilePrefix(prefix)
    setUnloadingMobile(value)
    if (prefix && value) {
      handleUnloadingChange("mobile", `${prefix}${value}`)
    }
    resetUnloadingErrorsFor(["phone", "mobile"])
  }

  const handleUnloadingCountryChange = (event: DropdownChangeParams) => {
    handleUnloadingChange("country", event.value)

    const prefix = constants?.results.countries
      .find((country) => country.value === event.value)?.prefix

    if (prefix) {
      setUnloadingPhonePrefix(prefix)
      setUnloadingMobilePrefix(prefix)
    }
  }

  const handleUnloadingChange = (key: keyof OrderUnloadingTemplate, value: any) => {
    setUnloading(Object.assign({}, unloading, { [key]: value }))
    resetUnloadingErrorsFor([key])
  }

  const [goods, setGoods] = useState<OrderGoodsTemplate[]>([
    { content: "", pieces: null, weight: null, packing: "", signification: "", length: null, width: null, height: null },
  ])

  const handleGoodsChange = (index: number, key: keyof OrderGoodsTemplate, value: any) => {
    setGoods(goods.map((good, i) => i === index
      ? Object.assign({}, good, { [key]: value })
      : good
    ))
    resetGoodsErrorsFor(index, [key])
  }

  const onGoodsItemDelete = (itemIndex: number) => {
    setGoods(goods.filter((_, index) => index !== itemIndex))
  }

  const onGoodsItemAdd = (): void => {
    setGoods([
      ...goods,
      { content: "", pieces: null, weight: null, packing: "", signification: "", length: null, width: null, height: null }
    ])
  }

  const [cod, setCod] = useState<boolean>(false)
  const handleCodChange = (event: CheckboxChangeParams) => {
    const isChecked = event.checked

    if (isChecked) {
      setCod(true)
    } else {
      setCod(false)
      handleCalculationCodAmountChange(null)
      handleCalculationCodVsChange(null)
      handleCalculationCodBankAccountChange("")
    }

  }
  const [calculation, setCalculation] = useState<OrderCalculationTemplate>({
    reference: "",
    deliveryNote: false,
    nonStackable: false,
    top: false,
    payer: false,
    cod: {
      amount: null,
      vs: null,
      bankAccount: "",
    },
    note: "",
    unloadingIn: "",
    unloadingVatId: "",
    loadingTime: "08:00-16:00",
  })

  const handleCalculationChange = (key: keyof OrderCalculationTemplate, value: any) => {
    setCalculation(Object.assign({}, calculation, { [key]: value }))
    resetCalculationErrorsFor([key])
  }

  const handleCalculationCodAmountChange = (value: number | null) => {
    const newCalculation = Object.assign({}, calculation)
    newCalculation.cod.amount = value
    setCalculation(newCalculation)
  }

  const handleCalculationCodVsChange = (value: number | null) => {
    const newCalculation = Object.assign({}, calculation)
    newCalculation.cod.vs = value
    setCalculation(newCalculation)
  }

  const handleCalculationCodBankAccountChange = (value: string) => {
    const newCalculation = Object.assign({}, calculation)
    newCalculation.cod.bankAccount = value
    setCalculation(newCalculation)
  }

  const tPackingItem = (option: GoodsPacking): ReactNode => (
    `${option.value} - ${option.label}`
  )

  const tPackingValue = (option: GoodsPacking, props: DropdownProps): ReactNode => (
    option
      ? `${option.value} - ${option.label}`
      : `${props.placeholder || "empty"}`
  )

  const { data: packings }: { data: GetOrderGoodsPackingsResponseData | undefined } =
    useGoodsPackings({
      loadingCountryCode: loading.country || "CZ",
      unloadingCountryCode: unloading.country || "CZ",
    })

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    if (uuid) {
      update()
    } else {
      create()
    }
  }

  const create = async () => {
    const payload: CreateOrderTemplatePayload = {
      name,
      isDefault: false,
      loading,
      unloading,
      goods,
      calculation,
    }

    const [isValid, errs] = validate(payload)
    setTemplateErrors(errs)

    if (isValid) {
      const resp = await createOrderTemplate.mutateAsync(payload)

      if (resp.errors.length > 0) {
        let errors = resp.errors as OrderTemplateError[];
        let respTemplateErrors: FormErrors<OrderTemplate> = {}
        let respLoadingErrors: FormErrors<OrderLoading> = {}
        let respUnloadingErrors: FormErrors<OrderUnloading> = {}
        let respGoodsErrors: FormErrorsArr<OrderGoods> = []
        let respCalculationErrors: FormErrors<OrderCalculation> = {}
        let respCalculationCodErrors: FormErrors<OrderCalculationCod> = {}

        goods.forEach(() => {
          respGoodsErrors.push({})
        })

        errors.forEach((error) => {
          if (typeof error.property === "string") {
            if (error.entity === "loading") {
              respLoadingErrors[error.property as keyof OrderLoading] = error.message
            } else if (error.entity === "unloading") {
              respUnloadingErrors[error.property as keyof OrderUnloading] = error.message
            } else if (error.entity === "goods") {
              respGoodsErrors[error.entityId][error.property as keyof OrderGoods] = error.message
            } else if (error.entity === "calculation") {
              respCalculationErrors[error.property as keyof OrderCalculation] = error.message
            } else if (typeof error.property === "string") {
              respTemplateErrors[error.property as keyof OrderTemplate] = error.message
            }
          } else {
            let errorCod = error.property;

            if (typeof errorCod.property === "string") {
              respCalculationCodErrors[errorCod.property as keyof OrderCalculationCod] = errorCod.message
            }
          }
        })

        setTemplateErrors(respTemplateErrors)
        setLoadingErrors(respLoadingErrors)
        setUnloadingErrors(respUnloadingErrors)
        setGoodsErrors(respGoodsErrors)
        setCalculationErrors(respCalculationErrors)
        setCalculationCodErrors(respCalculationCodErrors)

        scrollToFirstInvalidElement()
      } else {
        navigate("/order-templates")
      }
    }
  }

  const update = async () => {
    const payload: UpdateOrderTemplatePayload = {
      name,
      isDefault: false,
      loading,
      unloading,
      goods,
      calculation,
    }

    const [isValid, errs] = validate(payload)
    setTemplateErrors(errs)

    if (isValid) {
      const resp = await updateOrderTemplate.mutateAsync(payload)

      if (resp.errors.length > 0) {
        let errors = resp.errors as OrderTemplateError[];
        let respTemplateErrors: FormErrors<OrderTemplate> = {}
        let respLoadingErrors: FormErrors<OrderLoading> = {}
        let respUnloadingErrors: FormErrors<OrderUnloading> = {}
        let respGoodsErrors: FormErrorsArr<OrderGoods> = []
        let respCalculationErrors: FormErrors<OrderCalculation> = {}
        let respCalculationCodErrors: FormErrors<OrderCalculationCod> = {}

        goods.forEach(() => {
          respGoodsErrors.push({})
        })

        errors.forEach((error) => {
          if (typeof error.property === "string") {
            if (error.entity === "loading") {
              respLoadingErrors[error.property as keyof OrderLoading] = error.message
            } else if (error.entity === "unloading") {
              respUnloadingErrors[error.property as keyof OrderUnloading] = error.message
            } else if (error.entity === "goods") {
              respGoodsErrors[error.entityId][error.property as keyof OrderGoods] = error.message
            } else if (error.entity === "calculation") {
              respCalculationErrors[error.property as keyof OrderCalculation] = error.message
            } else if (typeof error.property === "string") {
              respTemplateErrors[error.property as keyof OrderTemplate] = error.message
            }
          } else {
            let errorCod = error.property;

            if (typeof errorCod.property === "string") {
              respCalculationCodErrors[errorCod.property as keyof OrderCalculationCod] = errorCod.message
            }
          }
        })

        setTemplateErrors(respTemplateErrors)
        setLoadingErrors(respLoadingErrors)
        setUnloadingErrors(respUnloadingErrors)
        setGoodsErrors(respGoodsErrors)
        setCalculationErrors(respCalculationErrors)
        setCalculationCodErrors(respCalculationCodErrors)

        scrollToFirstInvalidElement()
      } else {
        navigate("/order-templates")
      }
    }
  }

  const remove = async () => {
    await deleteOrderTemplate.mutateAsync()
    navigate("/order-templates")
  }

  const createOrder = async () => {
    const data = await createOrderTemplateOrder.mutateAsync()
    navigate(`/orders/${data.results.orderCreate.uuid}/loading`)
  }

  const resetErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setTemplateErrors(Object.assign({}, templateErrors, obj))
  }

  const resetLoadingErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setLoadingErrors(Object.assign({}, loadingErrors, obj))
  }

  const resetUnloadingErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setUnloadingErrors(Object.assign({}, unloadingErrors, obj))
  }

  const resetGoodsErrorsFor = (index: number, names: string[]) => {
    const newGoodsErrors = goodsErrors.map((errors, errorsIndex) => {
      if (index === errorsIndex) {
        names.forEach((name) => {
          errors[name as keyof OrderGoods] = ""
        })
      }
      return errors
    })

    setGoodsErrors(newGoodsErrors)
  }

  const resetCalculationErrorsFor = (names: string[]) => {
    let obj: { [key: string]: string } = {}

    names.forEach((name) => { obj[name] = "" })

    setCalculationErrors(Object.assign({}, calculationErrors, obj))
  }

  type AddressBookType = "loading" | "unloading"
  const [isAddressBookVisible, setIsAddressBookVisible] = useState<boolean>(false)
  const [addressBookType, setAddressBookType] = useState<AddressBookType | undefined>()
  const onAddressBookShow = (type: AddressBookType) => {
    setAddressBookType(type)
    setIsAddressBookVisible(true)
  }
  const onAddressBookHide = () => setIsAddressBookVisible(false)
  const onAddressBookSelect = (address: AddressSimple) => {
    if (addressBookType === "loading") {
      setLoading(Object.assign({}, loading, {
        name: address.name,
        street: address.street,
        streetDetails: address.streetDetails,
        zip: address.zip,
        city: address.city,
        country: address.country,
        person: address.person,
        email: address.email,
        phone: address.phone,
        mobile: address.mobile,
      }))

      const { prefix: lpp, number: lpn } = splitNumber(constants?.results.countries || [], address.phone || "")
      setLoadingPhonePrefix(lpp)
      setLoadingPhone(lpn)

      const { prefix: lmp, number: lmn } = splitNumber(constants?.results.countries || [], address.mobile || "")
      setLoadingMobilePrefix(lmp)
      setLoadingMobile(lmn)
    } else {
      setUnloading(Object.assign({}, unloading, {
        name: address.name,
        street: address.street,
        streetDetails: address.streetDetails,
        zip: address.zip,
        city: address.city,
        country: address.country,
        person: address.person,
        email: address.email,
        phone: address.phone,
        mobile: address.mobile,
      }))

      const { prefix: upp, number: upn } = splitNumber(constants?.results.countries || [], address.phone || "")
      setUnloadingPhonePrefix(upp)
      setUnloadingPhone(upn)

      const { prefix: ump, number: umn } = splitNumber(constants?.results.countries || [], address.mobile || "")
      setUnloadingMobilePrefix(ump)
      setUnloadingMobile(umn)
    }

    setIsAddressBookVisible(false)
  }

  const isForeignCountryTransport = loading.country !== "CZ" || unloading.country !== "CZ"

  return (
    <>
      <form className={"col-12"} onSubmit={handleFormSubmit}>
        <div className={"p-fluid"}>
          <div className={"block-title block-title_blue border-round-top px-3 py-2 flex justify-content-between h-auto align-items-center"}>
            Šablona
            {uuid && (
              <span>
                <Button type={"button"} label={"Vytvořit objednávku"}
                  className={"p-button p-button-sm p-button-secondary w-auto p-button-responsive mr-3"}
                  disabled={createOrderTemplateOrder.isLoading}
                  icon={createOrderTemplateOrder.isLoading ? "pi pi-spin pi-spinner" : "pi pi-plus"}
                  onClick={createOrder} />
                <Button type={"button"} label={"Smazat"}
                  className={"p-button p-button-sm p-button-danger w-auto p-button-responsive"}
                  icon={"pi pi-trash"} onClick={remove} />
              </span>
            )}
          </div>

          <div className={"border-solid border-1 border-300 px-3 pt-5 mb-6"}>
            <div className={"grid"}>
              <div className={"field col-12 md:col-4"}>
                <span className={"p-float-label"}>
                  <InputText value={name || ""} onChange={handleNameChange} required={true}
                    className={classNames({ "p-invalid": !!templateErrors?.name })} />
                  <label className={commonStyles.labelRequired}>Název:</label>
                </span>
                <FieldError error={templateErrors?.name} />
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue border-round-top flex align-items-center px-3"}>
            Nakládka

            <Button type={"button"} icon={"pi pi-book"} title={"Adresář"}
              className={"p-button-icon-only p-button-text ml-auto text-color"}
              onClick={() => onAddressBookShow("loading")} />
          </div>

          <div className={"border-solid border-1 border-300 p-3 mb-6"}>
            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Místo nakládky
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5 mb-3"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={loading.name || ""}
                      onChange={(e) => handleLoadingChange("name", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.name })} />
                    <label>Název odesílatele:</label>
                  </span>
                  <FieldError error={loadingErrors?.name} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={loading.street || ""}
                      onChange={(e) => handleLoadingChange("street", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.street })} />
                    <label>Ulice a číslo domu:</label>
                  </span>
                  <FieldError error={loadingErrors?.street} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={loading.streetDetails || ""}
                      onChange={(e) => handleLoadingChange("streetDetails", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.streetDetails })} />
                    <label>Upřesnění:</label>
                  </span>
                  <FieldError error={loadingErrors?.streetDetails} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={loading.zip || ""}
                      onChange={(e) => handleLoadingChange("zip", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.zip })} />
                    <label>PSČ:</label>
                  </span>
                  <FieldError error={loadingErrors?.zip} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={loading.city || ""}
                      onChange={(e) => handleLoadingChange("city", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.city })} />
                    <label>Obec:</label>
                  </span>
                  <FieldError error={loadingErrors?.city} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className="p-float-label">
                    <Dropdown options={constants?.results.countries || []}
                      value={loading.country || null}
                      onChange={handleLoadingCountryChange}
                      className={classNames({ "p-invalid": !!loadingErrors?.country })}
                      showClear={true}
                      itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate} />
                    <label>Stát:</label>
                  </span>
                  <FieldError error={loadingErrors?.country} />
                </div>
              </div>
            </div>

            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Kontaktní osoba
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5 mb-3"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"notifyPhone"} inputId={"notifyPhone"}
                      checked={loading.notifyPhone}
                      onChange={(e) => handleLoadingChange("notifyPhone", e.checked)} />
                    <label htmlFor={"notifyPhone"}>Avizovat nakládku telefonicky</label>
                  </div>
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={loading.person || ""}
                      onChange={(e) => handleLoadingChange("person", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.person })} />
                    <label>Jméno:</label>
                  </span>
                  <FieldError error={loadingErrors?.person} />
                </div>
              </div>

              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <PhoneInput placeholder={"Mobil:"} prefix={loadingMobilePrefix}
                    value={loadingMobile} onChange={handleLoadingMobileChange} isRequired={false}
                    error={loadingErrors?.mobile} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <PhoneInput placeholder={"Telefon:"} prefix={loadingPhonePrefix}
                    value={loadingPhone} onChange={handleLoadingPhoneChange} isRequired={false}
                    error={loadingErrors?.phone} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={loading.email || ""}
                      onChange={(e) => handleLoadingChange("email", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.email })} />
                    <label>E-mail:</label>
                  </span>
                  <FieldError error={loadingErrors?.email} />
                </div>
              </div>
            </div>

            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Důležité informace k nakládce
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText maxLength={30} value={loading.reference || ""}
                      onChange={(e) => handleLoadingChange("reference", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.reference })} />
                    <label>Reference k nakládce:</label>
                  </span>
                  <FieldError error={loadingErrors?.reference} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"tailgate"} inputId={"loading-tailgate"}
                      checked={loading.tailgate}
                      onChange={(e) => handleLoadingChange("tailgate", e.checked)} />
                    <label htmlFor={"loading-tailgate"}>Auto s čelem</label>
                  </div>
                </div>

                <div className={"field col-12"}>
                  <span className={"p-float-label"}>
                    <InputText maxLength={50} value={loading.note || ""}
                      onChange={(e) => handleLoadingChange("note", e.target.value)}
                      className={classNames({ "p-invalid": !!loadingErrors?.note })} />
                    <label>Dispozice k nakládce:</label>
                  </span>
                  <FieldError error={loadingErrors?.note} />
                </div>
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue border-round-top flex align-items-center px-3"}>
            Vykládka

            <Button type={"button"} icon={"pi pi-book"} title={"Adresář"}
              className={"p-button-icon-only p-button-text ml-auto text-color"}
              onClick={() => onAddressBookShow("unloading")} />
          </div>

          <div className={"border-solid border-1 border-300 p-3 mb-6"}>
            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Místo vykládky
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5 mb-3"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={unloading.name || ""}
                      onChange={(e) => handleUnloadingChange("name", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.name })}
                    />
                    <label>Název příjemce:</label>
                  </span>
                  <FieldError error={unloadingErrors?.name} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={unloading.street || ""}
                      onChange={(e) => handleUnloadingChange("street", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.street })} />
                    <label>Ulice a číslo domu:</label>
                  </span>
                  <FieldError error={unloadingErrors?.street} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={unloading.streetDetails || ""}
                      onChange={(e) => handleUnloadingChange("streetDetails", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.streetDetails })} />
                    <label>Upřesnění:</label>
                  </span>
                  <FieldError error={unloadingErrors?.streetDetails} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={unloading.zip || ""}
                      onChange={(e) => handleUnloadingChange("zip", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.zip })} />
                    <label>PSČ:</label>
                  </span>
                  <FieldError error={unloadingErrors?.zip} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={unloading.city || ""}
                      onChange={(e) => handleUnloadingChange("city", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.city })} />
                    <label>Obec:</label>
                  </span>
                  <FieldError error={unloadingErrors?.city} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className="p-float-label">
                    <Dropdown options={constants?.results.countries || []}
                      value={unloading.country || null}
                      onChange={handleUnloadingCountryChange}
                      className={classNames({ "p-invalid": !!unloadingErrors?.country })}
                      showClear={true}
                      itemTemplate={countryItemTemplate} valueTemplate={countryValueTemplate} />
                    <label>Stát:</label>
                  </span>
                  <FieldError error={unloadingErrors?.country} />
                </div>
              </div>
            </div>

            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Kontaktní osoba
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5 mb-3"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-8"}>
                  <label className={"mb-3"}>Avizování vykládky:</label>
                  <div className={"formgroup-inline"}>
                    <div className={"field-checkbox"}>
                      <Checkbox name={"phone"} inputId={"phone"}
                        checked={unloading.notifyPhone}
                        onChange={(e) => handleUnloadingChange("notifyPhone", e.checked)} />
                      <label htmlFor={"phone"}>Telefonicky</label>
                    </div>
                    <div className={"field-checkbox"}>
                      <Checkbox name={"sms"} inputId={"sms"}
                        checked={unloading.notifySMS}
                        onChange={(e) => handleUnloadingChange("notifySMS", e.checked)} />
                      <label htmlFor={"sms"}>Pomocí SMS</label>
                    </div>
                    <div className={"field-checkbox"}>
                      <Checkbox name={"email"} inputId={"email"}
                        checked={unloading.notifyEmail}
                        onChange={(e) => handleUnloadingChange("notifyEmail", e.checked)} />
                      <label htmlFor={"email"}>E-mailem</label>
                    </div>
                  </div>
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={unloading.person || ""}
                      onChange={(e) => handleUnloadingChange("person", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.person })} />
                    <label>Jméno:</label>
                  </span>
                  <FieldError error={unloadingErrors?.person} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <PhoneInput placeholder={"Mobil:"} prefix={unloadingMobilePrefix}
                    value={unloadingMobile} onChange={handleUnloadingMobileChange}
                    isRequired={false} error={unloadingErrors?.mobile} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <PhoneInput placeholder={"Telefon:"} prefix={unloadingPhonePrefix}
                    value={unloadingPhone} onChange={handleUnloadingPhoneChange}
                    isRequired={false} error={unloadingErrors?.phone} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={unloading.email || ""}
                      onChange={(e) => handleUnloadingChange("email", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.email })} />
                    <label>E-mail:</label>
                  </span>
                  <FieldError error={unloadingErrors?.email} />
                </div>
              </div>
            </div>

            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Důležité informace k vykládce
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText maxLength={30} value={unloading.reference || ""}
                      onChange={(e) => handleUnloadingChange("reference", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.reference })} />
                    <label>Reference k vykládce:</label>
                  </span>
                  <FieldError error={unloadingErrors?.reference} />
                </div>

                <div className={"field col-6 md:col-4"}>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"tailgate"} inputId={"unloading-tailgate"}
                      checked={unloading.tailgate}
                      onChange={(e) => handleUnloadingChange("tailgate", e.checked)} />
                    <label htmlFor={"unloading-tailgate"}>Auto s čelem</label>
                  </div>
                </div>

                <div className={"field col-12"}>
                  <span className={"p-float-label"}>
                    <InputText maxLength={50} value={unloading.note || ""}
                      onChange={(e) => handleUnloadingChange("note", e.target.value)}
                      className={classNames({ "p-invalid": !!unloadingErrors?.note })} />
                    <label>Dispozice k vykládce:</label>
                  </span>
                  <FieldError error={unloadingErrors?.note} />
                </div>
              </div>
            </div>
          </div>

          <div className={"block-title block-title_blue border-round-top px-3 py-2"}>
            Zásilka
          </div>

          <div className={"border-solid border-1 border-300 p-3 mb-6"}>
            {goods.map((good, index) => (
              <Fragment key={index}>
                <div className={"block-title block-title_green border-round-top px-3 flex align-items-center"}>
                  Položka č. {index + 1}

                  {goods.length > 1 && (
                    <Button icon={"pi pi-times"} type={"button"}
                      className={"p-button-text p-button-icon-only p-button-danger ml-auto"}
                      onClick={() => onGoodsItemDelete(index)}
                    />
                  )}
                </div>

                <div className={`border-solid border-1 border-300 px-3 pt-5 ${index === goods.length - 1 ? "" : "mb-3"}`}>
                  <div className={"grid"}>
                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputText value={goods[index].content || ""}
                          onChange={(e) => handleGoodsChange(index, "content", e.target.value)}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.content })} />
                        <label>Název zboží:</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.content} />
                    </div>

                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputNumber value={goods[index].pieces || null}
                          onChange={(e) => handleGoodsChange(index, "pieces", e.value)} min={1}
                          useGrouping={false}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.pieces })} />
                        <label>Počet kusů:</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.pieces} />
                      <small className={commonStyles.fieldHelp}>Jedná se např. o počet palet, nikoli kusů umístěných na jedné paletě.</small>
                    </div>

                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputText value={goods[index].signification || ""}
                          onChange={(e) => handleGoodsChange(index, "signification", e.target.value)}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.signification })} />
                        <label>Označení:</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.signification} />
                    </div>

                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputNumber value={goods[index].weight || null}
                          onChange={(e) => handleGoodsChange(index, "weight", e.value)} min={1}
                          useGrouping={false}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.weight })} />
                        <label>Hmotnost (kg):</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.weight} />
                      <small className={commonStyles.fieldHelp}>Jedná se o hmotnost všech kusů včetně palety (obalu) této položky.</small>
                    </div>

                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <Dropdown options={packings?.results.packings}
                          value={goods[index].packing || null}
                          onChange={(e) => handleGoodsChange(index, "packing", e.value)}
                          itemTemplate={tPackingItem} valueTemplate={tPackingValue}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.packing })}
                          showClear={true} />
                        <label>Obal:</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.packing} />
                    </div>
                  </div>

                  <div className={"grid"}>
                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputNumber value={goods[index].length || null}
                          onChange={(e) => handleGoodsChange(index, "length", e.value)} min={1}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.length })} />
                        <label>Délka (cm):</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.length} />
                    </div>

                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputNumber value={goods[index].width || null}
                          onChange={(e) => handleGoodsChange(index, "width", e.value)} min={1}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.width })} />
                        <label>Šířka (cm):</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.width} />
                    </div>

                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputNumber value={goods[index].height || null}
                          onChange={(e) => handleGoodsChange(index, "height", e.value)} min={1}
                          className={classNames({ "p-invalid": !!goodsErrors[index]?.height })} />
                        <label>Výška (cm):</label>
                      </span>
                      <FieldError error={goodsErrors[index]?.height} />
                    </div>
                  </div>
                </div>
              </Fragment>
            ))}

            <div className={"mb-3 text-center"}>
              {goods.length < MAX_GOODS_COUNT && (
                <Button onClick={onGoodsItemAdd} type={"button"}
                  className={`p-button-lg p-button-icon-only ${orderFormStyles.addNewButtonBlue}`}
                  icon={"pi pi-plus"} title={"Přidat položku"} />
              )}
            </div>
          </div>

          <div className={"block-title block-title_blue border-round-top px-3 py-2"}>
            Kalkulace
          </div>

          <div className={"border-solid border-1 border-300 p-3 mb-6"}>
            <div className={"py-3"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={calculation.reference || ""}
                      onChange={(e) => handleCalculationChange("reference", e.target.value)}
                      className={classNames({ "p-invalid": !!calculationErrors?.reference })} />
                    <label>Reference (z. č. objednávky)</label>
                  </span>
                  <FieldError error={calculationErrors?.reference} />
                </div>
              </div>
            </div>

            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Podmínky přepravy
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5 mb-3"}>
              <div className={"grid"}>
                {!isForeignCountryTransport && (
                  <div className={"field col-12 md:col-3"}>
                    <div className={"field-checkbox"}>
                      <Checkbox name={"deliveryNote"} inputId={"deliveryNote"}
                        checked={calculation.deliveryNote}
                        onChange={(e) => handleCalculationChange("deliveryNote", e.checked)} />
                      <label htmlFor={"deliveryNote"}>Potvrdit dodací list</label>
                    </div>
                  </div>
                )}

                <div className={"field col-12 md:col-3"}>
                  <div className={"field-checkbox"}>
                    <Checkbox name={"nonStackable"} inputId={"nonStackable"}
                      checked={calculation.nonStackable}
                      onChange={(e) => handleCalculationChange("nonStackable", e.checked)} />
                    <label htmlFor={"nonStackable"}>Nestohovat</label>
                  </div>
                </div>

                <div className={"field col-12 md:col-3"}>
                  <label className={"mb-3"}>Přepravu bude hradit:</label>
                  <div className={"formgroup-inline"}>
                    <div className={"field-radiobutton"}>
                      <RadioButton name={"payer"} inputId={"payer1"} value={false}
                        checked={!calculation.payer}
                        onChange={(e) => handleCalculationChange("payer", false)} />
                      <label htmlFor={"payer1"}>Objednavatel</label>
                    </div>
                    {!isForeignCountryTransport && (
                      <div className={"field-radiobutton"}>
                        <RadioButton name={"payer"} inputId={"payer2"} value={true}
                          checked={calculation.payer}
                          onChange={(e) => handleCalculationChange("payer", true)} />
                        <label htmlFor={"payer2"}>Příjemce</label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={calculation.unloadingIn || ""}
                      onChange={(e) => handleCalculationChange("unloadingIn", e.target.value)}
                      disabled={!calculation.payer}
                      className={classNames({ "p-invalid": !!calculationErrors?.unloadingIn })}
                    />
                    <label>IČ:</label>
                  </span>
                  <FieldError error={calculationErrors?.unloadingIn} />
                </div>

                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <InputText value={calculation.unloadingVatId || ""}
                      onChange={(e) => handleCalculationChange("unloadingVatId", e.target.value)}
                      disabled={!calculation.payer}
                      className={classNames({ "p-invalid": !!calculationErrors?.unloadingVatId })} />
                    <label>DIČ:</label>
                  </span>
                  <FieldError error={calculationErrors?.unloadingVatId} />
                </div>
              </div>
            </div>
            {!isForeignCountryTransport && (
              <>
                <div className={"block-title block-title_green border-round-top px-3 py-2"}>
                  Dobírka
                </div>

                <div className={"border-solid border-1 border-300 px-3 pt-5 mb-3"}>
                  <div className={"grid"}>
                    <div className={"field col-6 md:col-4"}>
                      <div className={"field-checkbox"}>
                        <Checkbox name={"cod"} inputId={"cod"}
                          checked={cod}
                          onChange={handleCodChange} />
                        <label htmlFor={"cod"}>Dobírka</label>
                      </div>
                    </div>
                  </div>
                  <div className={"grid"}>
                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputNumber disabled={!cod} value={calculation.cod.amount || null}
                          onChange={(e) => handleCalculationCodAmountChange(e.value)}
                          className={classNames({ "p-disabled": !cod, "p-invalid": !!calculationCodErrors?.amount })} />
                        <label>Částka (Kč):</label>
                      </span>
                      <FieldError error={calculationCodErrors?.amount} />
                    </div>
                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputNumber disabled={!cod} value={calculation.cod.vs || null}
                          onChange={(e) => handleCalculationCodVsChange(e.value)}
                          className={classNames({ "p-disabled": !cod, "p-invalid": !!calculationCodErrors?.vs })} />
                        <label>Variabilní symbol:</label>
                      </span>
                      <FieldError error={calculationCodErrors?.vs} />
                    </div>
                    <div className={"field col-12 md:col-4"}>
                      <span className={"p-float-label"}>
                        <InputText disabled={!cod} value={calculation.cod.bankAccount || ""}
                          onChange={(e) => handleCalculationCodBankAccountChange(e.target.value)}
                          className={classNames({ "p-invalid": !!calculationCodErrors?.bankAccount })} />
                        <label>Číslo účtu:</label>
                      </span>
                      <FieldError error={calculationCodErrors?.bankAccount} />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className={"block-title block-title_green border-round-top px-3 py-2"}>
              Nakládka
            </div>

            <div className={"border-solid border-1 border-300 px-3 pt-5"}>
              <div className={"grid"}>
                <div className={"field col-12 md:col-4"}>
                  <span className={"p-float-label"}>
                    <TimeRange onChange={(time) => handleCalculationChange("loadingTime", time)}
                      value={calculation.loadingTime} error={!!calculationErrors?.loadingTime} />
                    <label>Čas nakládky:</label>
                  </span>
                  <FieldError error={calculationErrors?.loadingTime} />
                </div>
              </div>
            </div>

            <div className={"pt-6"}>
              <div className={"grid"}>
                <div className={"col-12"}>
                  <span className={"p-float-label"}>
                    <InputTextarea value={calculation.note || ""}
                      onChange={(e) => handleCalculationChange("note", e.target.value)}
                      className={classNames({ "p-invalid": !!calculationErrors?.note })} />
                    <label>Poznámka pro DNP:</label>
                  </span>
                  <FieldError error={calculationErrors?.note} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={"text-right"}>
          <Button type={"submit"} label={"Uložit"}
            className={"p-button p-button-sm p-button-success"}
            disabled={updateOrderTemplate.isLoading}
            icon={"pi pi-save"} />
        </div>
      </form>

      <AddressBook isVisible={isAddressBookVisible} onHide={onAddressBookHide}
        onSelect={onAddressBookSelect} />
    </>
  )
}
